import React, { useMemo, useState, useEffect, useRef } from 'react';
import moment from 'moment';
import type { Moment } from 'moment';
import {
  TDateType,
  TimePickerType,
} from '@/pages/opinion/components/FilterCommon/types';
import RangePicker from '@/pages/home/components/RangePicker';
import {
  START_OF_WEEK,
  dailyFormat,
  dailyToDay,
  dayToDaily,
  defaultRangePickerValue,
  getHourMap,
  newOpinionOptionsMap,
  opinionOptionsMap,
  shortcutOption,
  timeRangeUserTranslate,
} from '../../../constants/date';
import { Bubble } from 'tea-component';
import './index.scss';
import { getLocale, useSelector } from 'umi';
import { getTimeLimitation, isTimeLimitation } from '../../../utils';
import useIntersectionObserver from '@/pages/opinion/pages/Summary/hooks/useIntersectionObserver';
import { getUtcTipsText } from '@/pages/opinion/utils/time';
import BubbleCommon from '@op/components/BubbleCommon';
import { LocaleType } from '@op/types/common';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';
import { addFilterBuriedPoint } from '@op/components/FilterCommon/utils/point';
import DBO_FWC from '@op/hooks/useFrontWebConfig';
import { ShortcutOptionType } from '@home/components/RangePicker/_config';
import useLanguage from '@op/hooks/useLanguage';

import { CONTACT_NAME } from '@op/constants';

const longDateTips =
  `Please select the time range within 3 year. Feel free to contact {contact} if needed`;

/**
 * 时间选择器
 * @param dateTypeInfo   dateType的数据与操作 [数据, 设置数据的方法]
 * @param timeValueInfo  time的数据与操作 [数据, 设置数据的方法]
 * @returns
 */
export default function TimePicker({
  dateTypeInfo,
  timeValueInfo,
  onFocus,
  lang,
  startOfTime,
  dateTypeList = ['date', 'hour', 'week', 'month'],
  rangePickerDefaultValue = defaultRangePickerValue,
  showShortcut = true,
  setConfirmDisable,
  noLimitation = false,
  wholeGameInfo,
  permanentTipsKey = 'common',
  setShortcutOption = (list: ShortcutOptionType) => list,
  rangePrefixMap,
  showTimeTips = true,
  maximumDuration,
  useNewsTypes = true,
  isSingleDay = false,
  defaultOptionsMap,
  disabledDate,
  disabledTips,
}: TimePickerType<string, Array<string>>) {
  const oriDateType = useRef<TDateType>('date');
  const [dateType, setDateType] = dateTypeInfo;
  const [timeValue, setTimeValue] = timeValueInfo;
  const [showError, setShowError] = useState(false);

  const { formatMessage } = useLanguage();
  const { userInfo } = useSelector((state: any) => {
    const {
      home: { userInfo },
    } = state;
    return { userInfo };
  });

  const maximumDurationValue: any = useMemo(() => {
    if (!maximumDuration)
      return {
        defaultNum: 3,
        defaultUnit: 'y',
        tips: formatMessage({ id: longDateTips }, { contact: CONTACT_NAME }),
      };

    return maximumDuration;
  }, [maximumDuration, formatMessage]);

  const fixedErrorTipsType = useRef<'max' | 'disabled'>('max');
  useEffect(() => {
    const { defaultNum, defaultUnit } = maximumDurationValue;
    if (isTimeLimitation(noLimitation, userInfo)) {
      const isFlag = getTimeLimitation(timeValue, defaultNum, defaultUnit);
      setConfirmDisable?.(isFlag);
      setShowError(isFlag);

      if (isFlag) {
        fixedErrorTipsType.current = 'max';
        return;
      }
    }

    const getDisabled = disabledDate?.[oriDateType.current];
    if (getDisabled) {
      const [startTime, endTime] = timeValue;
      const flag =
        getDisabled(moment(startTime)) && getDisabled(moment(endTime));
      fixedErrorTipsType.current = 'disabled';
      setConfirmDisable?.(!flag);
      setShowError(!flag);
    }
  }, [timeValue, userInfo, maximumDurationValue]);

  // 获取时间的开始和结束时间 如果改变日期，这里value会返回[null, null]
  const onChangeRangePicker = (value: Array<Moment>, type: string) => {
    onFocus?.();
    const localDateType = dayToDaily[type];
    setDateType(localDateType, type);
    oriDateType.current = type as TDateType;
    const hasTime = value.every(Boolean) && value.length > 0;
    const rangePickerValue = rangePickerDefaultValue();
    const newValue = hasTime
      ? value
      : rangePickerValue[dailyToDay[localDateType] || localDateType || 'date'];

    const newTimeValue = newValue.map((item) =>
      (typeof item === 'string' ? moment(item) : item).format(
        dailyFormat[localDateType],
      ),
    );

    if (isSingleDay) {
      if (newTimeValue[0] === timeValue[0]) {
        setTimeValue([newTimeValue[1], newTimeValue[1]]);
      } else {
        setTimeValue([newTimeValue[0], newTimeValue[0]]);
      }
    } else {
      setTimeValue(newTimeValue);
    }

    // console.log(type, dateType, localDateType);
    const isUpdateDate = type === dateType || dateType === localDateType;
    addFilterBuriedPoint(
      isUpdateDate ? FilterButtonPoint.date : FilterButtonPoint.dateType,
      isUpdateDate ? newTimeValue : localDateType,
    );
  };

  const rangePickerValue = useMemo(
    () => timeValue.map((time) => moment(time)),
    [timeValue],
  );

  const ref = useRef(null);

  const { show } = useIntersectionObserver({
    target: ref.current,
    threshold: 1,
  });

  const showErrorInfo = useMemo(() => {
    if (!showError) return null;
    if (!show) return null;

    if (fixedErrorTipsType.current === 'max') {
      return maximumDurationValue.tips;
    }

    if (disabledTips) {
      return formatMessage(disabledTips);
    }
  }, [showError, show, formatMessage, maximumDurationValue]);

  const rangePrefix = useMemo(() => {
    if (!rangePrefixMap) return {};
    const key: LocaleType = getLocale();
    return {
      rangePrefix: (
        <div
          className={
            key === 'zh-CN'
              ? 'dtb-rangepicker-label-prefix dtb-rangepicker-label-prefix-cn'
              : 'dtb-rangepicker-label-prefix'
          }
        >
          {rangePrefixMap[key]}
        </div>
      ),
    };
  }, [formatMessage]);

  const optionsMap = useMemo(() => {
    if (defaultOptionsMap) {
      return defaultOptionsMap;
    }

    if (isSingleDay) {
      return {
        '1day': { value: '1day', text: 'Day', text_cn: '日' },
      };
    }
    return useNewsTypes ? newOpinionOptionsMap : opinionOptionsMap;
  }, [useNewsTypes, isSingleDay]);

  return (
    <div
      ref={ref}
      className={`date-select-wrapper search-item search-item-date ${showShortcut ? 'date-select-wrapper-hour' : ''}`}
    >
      <Bubble
        visible={show}
        error={true}
        placementOffset={[50, 10]}
        content={showErrorInfo}
      >
        <BubbleCommon
          showIcon={false}
          placement="top"
          tipsMap={getUtcTipsText({
            show: !showError && showTimeTips && show,
            dateTipsKey: permanentTipsKey,
          })}
        >
          <div
            className={`${dateType === 'hourly'
              ? 'time-picker-hour-wrapper time-picker-wrapper'
              : 'time-picker-wrapper'
              } ${showError ? 'time-picker-wrapper-error' : ''}`}
          >
            <RangePicker
              autoStartDay={false}
              onChange={onChangeRangePicker}
              onFocus={onFocus}
              resultDateFormat={{
                Day: 'YYYY-MM-DD',
                Week: 'YYYY-MM-DD',
                Month: 'YYYY-MM-DD',
              }}
              defaultValue={rangePickerDefaultValue()}
              className={
                dateTypeList.length > 1 && !isSingleDay ? 'search-border' : ''
              }
              value={rangePickerValue}
              typeList={isSingleDay ? ['1day'] : dateTypeList}
              setShortcutOption={(list: ShortcutOptionType) => {
                const hourMap = getHourMap();
                let resultList: ShortcutOptionType = { ...list, ...hourMap };
                if (
                  DBO_FWC.get('has_release_time_shortcut') === 'yes' &&
                  wholeGameInfo?.release_time
                ) {
                  const resultMap = shortcutOption(
                    startOfTime || '2001-01-01',
                    {},
                    wholeGameInfo?.release_time,
                  );

                  resultList = { ...hourMap, ...resultMap };
                }
                return setShortcutOption?.(resultList);
              }}
              optionsMap={optionsMap}
              startOfWeek={START_OF_WEEK}
              showSelectType={isSingleDay ? false : dateTypeList.length > 1}
              disables={false}
              language={lang === 'zh-CN' ? 'cn' : 'en'}
              startOfTime={startOfTime || '2023-01-01'}
              type={isSingleDay ? '1day' : dailyToDay[dateType] || dateType}
              hourLeftSide={false}
              showShortcut={showShortcut}
              overlayClassName="dbo-time-picker-bubble-box"
              userTranslation={timeRangeUserTranslate}
              disabledDate={disabledDate}
              {...rangePrefix}
            />
          </div>
        </BubbleCommon>
      </Bubble>
    </div>
  );
}
